/**
 * Countries, in whatever language the person is reading.
 *
 * This used to read country names out of the emoji catalogue, which meant one
 * fixed set of English labels: somebody using the app in Polish saw "Slovenia"
 * in their statistics and had to search the emoji picker in English to find the
 * flag in the first place.
 *
 * `Intl.DisplayNames` already knows every country in every locale the browser
 * ships, so there is nothing to bundle and nothing to translate — "Słowenia",
 * "Slovenia" and "Slowenien" are the same two letters to us. The catalogue is
 * still consulted for *search*, because it carries city names that no locale
 * data has: somebody typing "Barcelona" means Spain, and no amount of country
 * data will tell you that.
 */

import { ALL_EMOJI } from "../components/ui/emojiCatalogue.js";

/** "SI" -> 🇸🇮. Two letters shifted into the regional-indicator block. */
export function flagFor(code: string): string {
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map(
      (letter) => 0x1f1e6 + letter.charCodeAt(0) - 65,
    ),
  );
}

/** The ISO code inside a flag emoji, or null if it is not one. */
export function codeFromFlag(emoji: string | null | undefined): string | null {
  if (!emoji) return null;

  const points = [...emoji];
  if (points.length !== 2) return null;

  let code = "";
  for (const point of points) {
    const value = point.codePointAt(0);
    if (value === undefined || value < 0x1f1e6 || value > 0x1f1ff) return null;
    code += String.fromCharCode(value - 0x1f1e6 + 65);
  }
  return code;
}

/*
 * One `Intl.DisplayNames` per locale, kept.
 *
 * Constructing one is not free — it loads locale data — and the country picker
 * would otherwise build a new one for every country on every keystroke.
 */
const displayNames = new Map<string, Intl.DisplayNames | null>();

function namesFor(locale: string): Intl.DisplayNames | null {
  const existing = displayNames.get(locale);
  if (existing !== undefined) return existing;

  let made: Intl.DisplayNames | null = null;
  try {
    made = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    /* An unsupported locale, or a browser without the API. Falls back below. */
  }
  displayNames.set(locale, made);
  return made;
}

/**
 * The country's name in this locale, falling back to English, then the code.
 *
 * Never null: a two-letter code is a worse label than a name and a better one
 * than nothing, and the caller has no better answer to substitute.
 */
export function countryName(code: string, locale = "en"): string {
  const upper = code.toUpperCase();
  return namesFor(locale)?.of(upper) ?? namesFor("en")?.of(upper) ?? upper;
}

/**
 * Every ISO 3166-1 alpha-2 code the browser knows, found by asking it.
 *
 * Not taken from the emoji catalogue, which was the first attempt: that list is
 * a curated 117 — the places people travel to — and a country *picker* that
 * cannot offer Andorra is a picker that is wrong about somebody's holiday.
 *
 * Not written out either. A hardcoded list of 250 codes is a list that goes
 * stale, and it would then disagree with the names `Intl.DisplayNames` gives
 * for them. Probing every AA–ZZ pair and keeping the ones that resolve to a
 * name asks the same source that will do the naming: 676 lookups, 11 ms, once.
 */
export const COUNTRY_CODES: readonly string[] = (() => {
  const names = namesFor("en");
  if (!names) return [];

  const codes: string[] = [];
  for (let first = 65; first <= 90; first += 1) {
    for (let second = 65; second <= 90; second += 1) {
      const code = String.fromCharCode(first, second);
      let name: string | undefined;
      try {
        name = names.of(code);
      } catch {
        continue;
      }
      /* An unknown region comes back as the code itself, not as undefined. */
      if (!name || name === code) continue;

      /*
       * Skip codes that have been retired into another one.
       *
       * ICU still answers for DD, YU, ZR and a dozen others, and it answers
       * with the *modern* country's name — so DD is “Germany”, sorts before DE,
       * and a search for Germany offers East Germany. `maximize()` resolves an
       * alias to what it became, so a code that does not resolve to itself is
       * one of them.
       */
      try {
        if (new Intl.Locale(`und-${code}`).maximize().region !== code) continue;
      } catch {
        continue;
      }

      codes.push(code);
    }
  }
  return codes;
})();

export interface CountryMatch {
  code: string;
  name: string;
}

/** Fold accents and case so "slowenia" finds "Słowenia" and "espana" "España". */
function fold(value: string): string {
  return (
    value
      .toLocaleLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      /* Ł does not decompose, so it survives NFD and has to be named. */
      .replace(/ł/g, "l")
      .trim()
  );
}

/**
 * Countries matching what somebody typed, in their language or in English.
 *
 * Both, always. Somebody reading the app in Polish may still type "Germany"
 * because that is what the place is called on their boarding pass, and
 * somebody reading it in English may type "Deutschland" for the same reason.
 * Names that *start* with the query come first — typing "ni" should offer
 * Nicaragua before Bosnia — and an exact match comes before everything.
 */
export function searchCountries(
  query: string,
  locale = "en",
  limit = 8,
): CountryMatch[] {
  const needle = fold(query);
  if (needle.length === 0) return [];

  const scored: { match: CountryMatch; score: number }[] = [];

  for (const code of COUNTRY_CODES) {
    const local = countryName(code, locale);
    const english = countryName(code, "en");

    const candidates = [fold(local), fold(english), code.toLowerCase()];
    let best = 0;

    for (const candidate of candidates) {
      if (candidate === needle) best = Math.max(best, 3);
      else if (candidate.startsWith(needle)) best = Math.max(best, 2);
      else if (candidate.includes(needle)) best = Math.max(best, 1);
    }

    if (best > 0) scored.push({ match: { code, name: local }, score: best });
  }

  return scored
    .sort(
      (a, b) => b.score - a.score || a.match.name.localeCompare(b.match.name),
    )
    .slice(0, limit)
    .map((entry) => entry.match);
}

/**
 * The country somebody has already named without meaning to.
 *
 * Album names are things like "Słowenia 2026" and "Spain with Kasia", and
 * locations are "Kraków" or "Barcelona". Rather than asking for a country a
 * second time, this reads one out of what has been typed — matching whole words
 * only, so "Chile" is not found inside "children" and "Mali" not inside
 * "Malinowski".
 *
 * Falls through to the emoji catalogue's keywords, which carry the cities
 * people photograph most. That is where "Barcelona" becomes Spain; no locale
 * data anywhere knows that.
 */
export function detectCountry(
  text: string,
  locale = "en",
): CountryMatch | null {
  const folded = fold(text);
  if (folded.length < 3) return null;

  const words = new Set(folded.split(/[^\p{L}\p{N}]+/u).filter(Boolean));

  /* Country names first, and the longest match wins: "United States" over
     "United" would matter if any country were a prefix of another's word. */
  let best: { match: CountryMatch; length: number } | null = null;

  for (const code of COUNTRY_CODES) {
    for (const locales of [locale, "en"]) {
      const name = fold(countryName(code, locales));
      if (name.length < 3) continue;

      const found = name.includes(" ")
        ? folded.includes(name)
        : words.has(name);

      if (found && (!best || name.length > best.length)) {
        best = {
          match: { code, name: countryName(code, locale) },
          length: name.length,
        };
      }
    }
  }

  if (best) return best.match;

  /*
   * A city, then. Only exact whole words: the catalogue's keyword lists are
   * short and generic in places ("sun", "sea"), and a substring match against
   * those would put a country on every summer album.
   */
  for (const entry of ALL_EMOJI) {
    const code = codeFromFlag(entry.emoji);
    if (!code) continue;

    for (const keyword of entry.keywords) {
      const folded_keyword = fold(keyword);
      if (folded_keyword.length >= 4 && words.has(folded_keyword)) {
        return { code, name: countryName(code, locale) };
      }
    }
  }

  return null;
}

/**
 * The one flag to draw where only one fits.
 *
 * An album can name several countries now, and a sidebar row has room for one
 * glyph. The first is the answer rather than the alphabetically-first, because
 * the order the countries were entered in is itself the answer to "which of
 * these is this album mostly about".
 */
export function primaryCountry(
  codes: string[] | null | undefined,
): string | null {
  return codes?.[0] ?? null;
}
